import { Component, type ErrorInfo, type ReactNode } from "react";

import { reportClientError } from "../lib/errorReporter.ts";

/**
 * Minimal error boundary so a render error in one panel can't white-screen the whole
 * app — important on the live meeting page, where a crash mid-call is costly. Shows a
 * small inline fallback and logs the error; the rest of the page keeps working.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode; label?: string },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[error-boundary]${this.props.label ? ` ${this.props.label}` : ""}`, error, info);
    void reportClientError({
      kind: "react",
      message: error?.message || "React render error",
      stack: error?.stack || info?.componentStack || undefined,
      extra: this.props.label ? { label: this.props.label } : undefined,
    });
  }

  override render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div style={{ padding: 16, fontSize: 13, color: "var(--text-3)" }}>
            Something went wrong displaying this section. The rest of the meeting is unaffected.
          </div>
        )
      );
    }
    return this.props.children;
  }
}
